import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  Typography,
  Box,
  Alert,
  Stepper,
  Step,
  StepLabel,
  Paper,
  Card,
  CardContent,
  Stack,
  Chip,
  Divider,
  IconButton,
  Tooltip,
} from '@mui/material';
import {
  DirectionsCar as CarIcon,
  Person as PersonIcon,
  Schedule as ScheduleIcon,
  LocationOn as LocationIcon,
  Warning as WarningIcon,
  CheckCircle as CheckIcon,
  Info as InfoIcon,
  Refresh as RefreshIcon,
} from '@mui/icons-material';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import { vehicleRequestAPI, calendarAPI } from '../services/api';

interface Vehicle {
  _id: string;
  plateNumber: string;
  make: string;
  model: string;
  category: string;
  availability: 'available' | 'unavailable' | 'partially_available';
  conflictingRequests?: Array<{
    requestId: string;
    purpose: string;
    startTime: string;
    endTime: string;
    requester: string;
  }>;
}

interface ConflictingRequest {
  _id: string;
  startDateTime: string;
  endDateTime: string;
  purpose: string;
  requestedBy: {
    firstName: string;
    lastName: string;
  };
  assignedVehicle?: {
    plateNumber: string;
    make: string;
    model: string;
  };
}

interface RequestFormData {
  purpose: string;
  destination: string;
  startDateTime: Date;
  endDateTime: Date;
  passengerCount: number;
  vehicleCategory: string;
  specialRequirements: string;
  priority: 'low' | 'medium' | 'high' | 'urgent';
}

interface EnhancedRequestFormProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (data: RequestFormData) => Promise<void>;
}

const EnhancedRequestForm: React.FC<EnhancedRequestFormProps> = ({
  open,
  onClose,
  onSubmit
}) => {
  const [activeStep, setActiveStep] = useState(0);
  const [formData, setFormData] = useState<RequestFormData>({
    purpose: '',
    destination: '',
    startDateTime: new Date(),
    endDateTime: new Date(Date.now() + 4 * 60 * 60 * 1000), // 4 hours later
    passengerCount: 1,
    vehicleCategory: 'any',
    specialRequirements: '',
    priority: 'medium'
  });

  const [availableVehicles, setAvailableVehicles] = useState<Vehicle[]>([]);
  const [conflicts, setConflicts] = useState<ConflictingRequest[]>([]);
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [availabilityChecked, setAvailabilityChecked] = useState(false);

  const steps = ['Request Details', 'Schedule & Vehicle', 'Review & Submit'];

  // Check availability when date/time or category changes
  useEffect(() => {
    if (formData.startDateTime && formData.endDateTime && activeStep === 1) {
      checkAvailability();
    }
  }, [formData.startDateTime, formData.endDateTime, formData.vehicleCategory, activeStep]);

  const checkAvailability = async () => {
    try {
      setLoading(true);
      
      // Check for conflicts
      const conflictResults = await calendarAPI.checkConflicts(
        formData.startDateTime.toISOString(),
        formData.endDateTime.toISOString()
      );
      setConflicts(conflictResults);

      // Get available vehicles - Fixed type issue by creating a new interface
      const vehicles = await vehicleRequestAPI.getAvailableVehicles(
        formData.startDateTime.toISOString(),
        formData.endDateTime.toISOString(),
        formData.vehicleCategory !== 'any' ? formData.vehicleCategory : undefined
      );
      
      // Map the vehicles to ensure they have the availability property
      const vehiclesWithAvailability: Vehicle[] = vehicles.map(vehicle => ({
        ...vehicle,
        availability: 'available' as 'available' | 'unavailable' | 'partially_available',
        conflictingRequests: []
      }));
      
      setAvailableVehicles(vehiclesWithAvailability);
      setAvailabilityChecked(true);

    } catch (error) {
      console.error('Error checking availability:', error);
    } finally {
      setLoading(false);
    }
  };

  const validateStep = (step: number): boolean => {
    const newErrors: Record<string, string> = {};

    if (step === 0) {
      if (!formData.purpose.trim()) {
        newErrors.purpose = 'Purpose is required';
      }
      if (!formData.destination.trim()) {
        newErrors.destination = 'Destination is required';
      }
      if (formData.passengerCount < 1) {
        newErrors.passengerCount = 'At least 1 passenger required';
      }
      if (formData.passengerCount > 50) {
        newErrors.passengerCount = 'Maximum 50 passengers allowed';
      }
    }

    if (step === 1) {
      if (formData.startDateTime <= new Date()) {
        newErrors.startDateTime = 'Start date must be in the future';
      }
      if (formData.endDateTime <= formData.startDateTime) {
        newErrors.endDateTime = 'End date must be after start date';
      }
      if (availabilityChecked && availableVehicles.length === 0 && formData.vehicleCategory !== 'any') {
        newErrors.vehicleCategory = 'No vehicles available in selected category for this time slot';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleNext = () => {
    if (validateStep(activeStep)) {
      setActiveStep((prev) => prev + 1);
    }
  };

  const handleBack = () => {
    setActiveStep((prev) => prev - 1);
  };

  const handleSubmit = async () => {
    if (validateStep(2)) {
      try {
        setLoading(true);
        await onSubmit(formData);
        handleReset();
        onClose();
      } catch (error) {
        console.error('Error submitting request:', error);
      } finally {
        setLoading(false);
      }
    }
  };

  const handleReset = () => {
    setActiveStep(0);
    setFormData({
      purpose: '',
      destination: '',
      startDateTime: new Date(),
      endDateTime: new Date(Date.now() + 4 * 60 * 60 * 1000),
      passengerCount: 1,
      vehicleCategory: 'any',
      specialRequirements: '',
      priority: 'medium'
    });
    setAvailableVehicles([]);
    setConflicts([]);
    setErrors({});
    setAvailabilityChecked(false);
  };

  const formatDateTime = (date: Date) => {
    return date.toLocaleDateString() + ' at ' + date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  const getCategoryLabel = (category: string) => {
    switch (category) {
      case 'executive': return 'Executive Pool';
      case 'staff_bus': return 'Staff Bus';
      case 'technical': return 'Technical Units';
      case 'general': return 'General Pool';
      case 'any': return 'Any Available';
      default: return category;
    }
  };

  const getPriorityColor = (priority: string): 'default' | 'primary' | 'secondary' | 'error' | 'info' | 'success' | 'warning' => {
    switch (priority) {
      case 'urgent': return 'error';
      case 'high': return 'warning';
      case 'medium': return 'info';
      case 'low': return 'default';
      default: return 'default';
    }
  };

  const renderStepContent = (step: number) => {
    switch (step) {
      case 0:
        return (
          <Stack spacing={3}>
            <TextField
              fullWidth
              required
              label="Purpose of Request"
              value={formData.purpose}
              onChange={(e) => setFormData({ ...formData, purpose: e.target.value })}
              error={!!errors.purpose}
              helperText={errors.purpose}
              multiline
              rows={3}
              placeholder="e.g., Client meeting, site inspection, staff transport..."
            />

            <TextField
              fullWidth
              required
              label="Destination"
              value={formData.destination}
              onChange={(e) => setFormData({ ...formData, destination: e.target.value })}
              error={!!errors.destination}
              helperText={errors.destination}
              placeholder="e.g., Lagos Island, Victoria Island, Airport..."
            />

            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  required
                  type="number"
                  label="Number of Passengers"
                  value={formData.passengerCount}
                  onChange={(e) => setFormData({ ...formData, passengerCount: parseInt(e.target.value) || 1 })}
                  error={!!errors.passengerCount}
                  helperText={errors.passengerCount}
                  inputProps={{ min: 1, max: 50 }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth>
                  <InputLabel>Priority</InputLabel>
                  <Select
                    value={formData.priority}
                    onChange={(e) => setFormData({ ...formData, priority: e.target.value as 'low' | 'medium' | 'high' | 'urgent' })}
                  >
                    <MenuItem value="low">Low</MenuItem>
                    <MenuItem value="medium">Medium</MenuItem>
                    <MenuItem value="high">High</MenuItem>
                    <MenuItem value="urgent">Urgent</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
            </Grid>

            <TextField
              fullWidth
              label="Special Requirements (Optional)"
              value={formData.specialRequirements}
              onChange={(e) => setFormData({ ...formData, specialRequirements: e.target.value })}
              multiline
              rows={2}
              placeholder="e.g., wheelchair accessible, air conditioning, specific driver..."
            />
          </Stack>
        );

      case 1:
        return (
          <Stack spacing={3}>
            <LocalizationProvider dateAdapter={AdapterDateFns}>
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <DateTimePicker
                    label="Start Date & Time"
                    value={formData.startDateTime}
                    onChange={(newValue) => {
                      if (newValue) {
                        setFormData({ ...formData, startDateTime: newValue });
                        setAvailabilityChecked(false);
                      }
                    }}
                    slotProps={{
                      textField: {
                        fullWidth: true,
                        error: !!errors.startDateTime,
                        helperText: errors.startDateTime
                      }
                    }}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <DateTimePicker
                    label="End Date & Time"
                    value={formData.endDateTime}
                    onChange={(newValue) => {
                      if (newValue) {
                        setFormData({ ...formData, endDateTime: newValue });
                        setAvailabilityChecked(false);
                      }
                    }}
                    slotProps={{
                      textField: {
                        fullWidth: true,
                        error: !!errors.endDateTime,
                        helperText: errors.endDateTime
                      }
                    }}
                  />
                </Grid>
              </Grid>
            </LocalizationProvider>

            <FormControl fullWidth>
              <InputLabel>Vehicle Category</InputLabel>
              <Select
                value={formData.vehicleCategory}
                onChange={(e) => {
                  setFormData({ ...formData, vehicleCategory: e.target.value });
                  setAvailabilityChecked(false);
                }}
                error={!!errors.vehicleCategory}
              >
                <MenuItem value="any">Any Available</MenuItem>
                <MenuItem value="executive">Executive Pool</MenuItem>
                <MenuItem value="staff_bus">Staff Bus</MenuItem>
                <MenuItem value="technical">Technical Units</MenuItem>
                <MenuItem value="general">General Pool</MenuItem>
              </Select>
            </FormControl>

            {errors.vehicleCategory && (
              <Alert severity="error">{errors.vehicleCategory}</Alert>
            )}

            <Box>
              <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 2 }}>
                <Typography variant="h6">Vehicle Availability</Typography>
                <Tooltip title="Check availability for selected time slot">
                  <IconButton onClick={checkAvailability} disabled={loading}>
                    <RefreshIcon />
                  </IconButton>
                </Tooltip>
              </Stack>

              {loading && (
                <Box sx={{ textAlign: 'center', py: 2 }}>
                  <Typography>Checking availability...</Typography>
                </Box>
              )}

              {availabilityChecked && !loading && (
                <Stack spacing={2}>
                  {availableVehicles.length > 0 ? (
                    <Alert severity="success" icon={<CheckIcon />}>
                      {availableVehicles.length} vehicle(s) available in {getCategoryLabel(formData.vehicleCategory)}
                    </Alert>
                  ) : (
                    <Alert severity="warning" icon={<WarningIcon />}>
                      No vehicles available in {getCategoryLabel(formData.vehicleCategory)} for this time slot
                    </Alert>
                  )}

                  {conflicts.length > 0 && (
                    <Paper sx={{ p: 2, bgcolor: 'rgba(255, 152, 0, 0.1)' }}>
                      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
                        <InfoIcon color="warning" />
                        <Typography variant="subtitle2" color="warning.dark">
                          Scheduling Conflicts Detected
                        </Typography>
                      </Stack>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                        The following requests overlap with your selected time:
                      </Typography>
                      {conflicts.slice(0, 3).map((conflict) => (
                        <Chip
                          key={conflict._id}
                          size="small"
                          label={`${conflict.requestedBy.firstName} ${conflict.requestedBy.lastName} - ${conflict.purpose.substring(0, 30)}...`}
                          sx={{ m: 0.5 }}
                        />
                      ))}
                      {conflicts.length > 3 && (
                        <Typography variant="caption" color="text.secondary">
                          +{conflicts.length - 3} more conflicts
                        </Typography>
                      )}
                    </Paper>
                  )}
                </Stack>
              )}
            </Box>
          </Stack>
        );

      case 2:
        return (
          <Stack spacing={3}>
            <Typography variant="h6">Review Your Request</Typography>
            
            <Paper sx={{ p: 3, bgcolor: 'grey.50' }}>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <Typography variant="body2" color="text.secondary">Purpose:</Typography>
                  <Typography variant="body1" sx={{ fontWeight: 'bold', mb: 1 }}>
                    {formData.purpose}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="body2" color="text.secondary">Destination:</Typography>
                  <Typography variant="body1" sx={{ fontWeight: 'bold', mb: 1 }}>
                    {formData.destination}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="body2" color="text.secondary">Date & Time:</Typography>
                  <Typography variant="body1" sx={{ fontWeight: 'bold', mb: 1 }}>
                    {formatDateTime(formData.startDateTime)}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    to {formatDateTime(formData.endDateTime)}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="body2" color="text.secondary">Passengers:</Typography>
                  <Typography variant="body1" sx={{ fontWeight: 'bold', mb: 1 }}>
                    {formData.passengerCount}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="body2" color="text.secondary">Vehicle Category:</Typography>
                  <Typography variant="body1" sx={{ fontWeight: 'bold', mb: 1 }}>
                    {getCategoryLabel(formData.vehicleCategory)}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="body2" color="text.secondary">Priority:</Typography>
                  <Chip
                    size="small"
                    color={getPriorityColor(formData.priority)}
                    label={formData.priority}
                    variant="filled"
                  />
                </Grid>
                {formData.specialRequirements && (
                  <Grid item xs={12}>
                    <Typography variant="body2" color="text.secondary">Special Requirements:</Typography>
                    <Typography variant="body1" sx={{ fontStyle: 'italic' }}>
                      {formData.specialRequirements}
                    </Typography>
                  </Grid>
                )}
              </Grid>
            </Paper>

            {availabilityChecked && availableVehicles.length > 0 && (
              <Alert severity="success">
                Your request can be accommodated. {availableVehicles.length} vehicle(s) are available for your selected time slot.
              </Alert>
            )}

            {availabilityChecked && availableVehicles.length === 0 && (
              <Alert severity="warning">
                No vehicles are available in the selected category for your chosen time slot. 
                Your request will still be submitted and may be approved with an alternative vehicle or time adjustment.
              </Alert>
            )}

            {conflicts.length > 0 && (
              <Alert severity="info">
                There are {conflicts.length} overlapping requests during your selected time. 
                The fleet manager will coordinate to ensure optimal vehicle assignment.
              </Alert>
            )}
          </Stack>
        );

      default:
        return <div>Unknown step</div>;
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Typography variant="h6">New Vehicle Request</Typography>
          <Chip
            size="small"
            color={getPriorityColor(formData.priority)}
            label={`${formData.priority} priority`}
            variant="outlined"
          />
        </Box>
      </DialogTitle>

      <DialogContent>
        <Box sx={{ mt: 2 }}>
          <Stepper activeStep={activeStep} sx={{ mb: 4 }}>
            {steps.map((label) => (
              <Step key={label}>
                <StepLabel>{label}</StepLabel>
              </Step>
            ))}
          </Stepper>

          {renderStepContent(activeStep)}
        </Box>
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        
        <Box sx={{ flex: '1 1 auto' }} />
        
        {activeStep !== 0 && (
          <Button onClick={handleBack}>
            Back
          </Button>
        )}
        
        {activeStep < steps.length - 1 ? (
          <Button variant="contained" onClick={handleNext}>
            Next
          </Button>
        ) : (
          <Button
            variant="contained"
            onClick={handleSubmit}
            disabled={loading}
            color={availableVehicles.length > 0 ? 'primary' : 'warning'}
          >
            {loading ? 'Submitting...' : 'Submit Request'}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default EnhancedRequestForm;